import React, { useEffect, useRef, useState } from "react";
import { normalizeColumnOrder } from "./columnOrder";
import "./colonneTabella.css";

export default function ColonneTabellaPanel({
  preference, sortField, sortDirection, metadata, columnIds, lockedIds,
  idPrefix, ariaLabel = "Colonne della tabella", selectionColumn = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const buttonRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const close = () => {
    setOpen(false);
    buttonRef.current?.focus();
  };
  const query = search.trim().toLocaleLowerCase("it");
  const matches = (meta) => !query || [meta.label, ...(meta.aliases || [])]
    .some((value) => value.toLocaleLowerCase("it").includes(query));
  const mandatory = metadata.filter((meta) => meta.locked && matches(meta));
  const showFlag = selectionColumn && (!query || "flag di selezione".includes(query) || "checkbox".includes(query));
  const orderedIds = normalizeColumnOrder(preference.order, columnIds, lockedIds);
  const optionalAll = orderedIds.map((id) => metadata.find((meta) => meta.id === id))
    .filter((meta) => !meta.locked);
  const optional = optionalAll.filter(matches);
  const move = (id, offset) => {
    const next = [...orderedIds];
    const index = next.indexOf(id);
    [next[index], next[index + offset]] = [next[index + offset], next[index]];
    preference.reorder(next);
  };
  const hasHiddenColumns = metadata.some((meta) => !meta.locked && preference.columns[meta.id] === false);
  const sorted = metadata.find((meta) => (meta.sortField ?? meta.header) === sortField);
  const statusText = {
    loading: "Caricamento colonne…",
    saving: "Salvo…",
    saved: "Salvato",
    error: "Salvataggio non riuscito: scelta valida solo in questa sessione",
    loadError: "Preferenze non caricate: riprova ricaricando la pagina",
  }[preference.status];

  return (
    <div className="ordini-colonne-toolbar">
      <div className="ordini-colonne-control">
        <button ref={buttonRef} type="button" className="dim tastoInputOrizzontale br3 ordini-colonne-button"
          title="Imposta colonne" aria-label="Imposta colonne" data-active={hasHiddenColumns}
          aria-expanded={open} aria-controls={`${idPrefix}-panel`}
          onClick={() => open ? close() : setOpen(true)}>
          <span className="material-icons colScrittaDef-20" aria-hidden="true">view_column</span>
        </button>
        {open && (
          <div id={`${idPrefix}-panel`} className="ordini-colonne-panel"
            role="dialog" aria-label={ariaLabel}>
            <div className="ordini-colonne-panel-top">
              <strong>Colonne</strong>
              <span className="ordini-colonne-status" role="status" aria-atomic="true" title={statusText}>
                {statusText}
              </span>
              <button type="button" onClick={close} title="Chiudi pannello colonne" aria-label="Chiudi pannello colonne">×</button>
            </div>
            <label htmlFor={`${idPrefix}-search`}>Cerca una colonna…</label>
            <input ref={searchRef} id={`${idPrefix}-search`} type="search"
              value={search} onChange={(event) => setSearch(event.target.value)}
              placeholder="Cerca una colonna…" />
            {(mandatory.length > 0 || showFlag) && <div className="ordini-colonne-group">
              <strong>Sempre visibili</strong>
              {showFlag && <div className="ordini-colonne-fixed">☑ Flag di selezione <span>Bloccata</span></div>}
              {mandatory.map((meta) => <div key={meta.id} className="ordini-colonne-fixed">
                ☑ {meta.label} <span>Bloccata</span>
              </div>)}
            </div>}
            <div className="ordini-colonne-group">
              <strong>Altre colonne</strong>
              <small>Riordina con le frecce o trascinando le intestazioni della tabella.</small>
              {query && <small>Le frecce spostano la colonna nell'elenco completo.</small>}
              {optional.length ? optional.map((meta) => (
                <div key={meta.id} className="ordini-colonne-choice-row">
                  <label className="ordini-colonne-choice">
                    <input type="checkbox" disabled={!preference.ready}
                      checked={preference.columns[meta.id] !== false}
                      onChange={(event) => preference.change(meta.id, event.target.checked)} />
                    <span>{meta.label}</span>
                  </label>
                  <div className="ordini-colonne-move">
                    <button type="button" aria-label={`Sposta ${meta.label} su`} title={`Sposta ${meta.label} su`}
                      disabled={!preference.ready || optionalAll[0].id === meta.id}
                      onClick={() => move(meta.id, -1)}>↑</button>
                    <button type="button" aria-label={`Sposta ${meta.label} giù`} title={`Sposta ${meta.label} giù`}
                      disabled={!preference.ready || optionalAll[optionalAll.length - 1].id === meta.id}
                      onClick={() => move(meta.id, 1)}>↓</button>
                  </div>
                </div>
              )) : <span>Nessuna colonna trovata.</span>}
            </div>
            <button type="button" className="ordini-colonne-reset" title="Ripristina colonne predefinite" disabled={!preference.ready}
              onClick={preference.reset}>Ripristina colonne predefinite</button>
          </div>
        )}
      </div>
      {sorted && sortDirection && preference.columns[sorted.id] === false && <span className="ordini-colonne-sort">
        Ordinamento: {sorted.label} {sortDirection === "asc" ? "↑" : "↓"}
      </span>}
    </div>
  );
}
